import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  scottishSchoolService,
  type ScottishCouncil,
  type ScottishSchool,
  type ScottishSchoolWithCouncil,
} from '../../services/scottishSchoolService';
import UnknownCouncilSchoolPicker from './UnknownCouncilSchoolPicker';

const UNKNOWN_COUNCIL = '__UNKNOWN_COUNCIL__';
const SCHOOL_NOT_LISTED = '__SCHOOL_NOT_LISTED__';
import {
  parentCourseService,
  type ChildSchoolProfileSummary,
  type ChildSchoolProfileUpdateRequest,
} from '../../services/parentCourseService';

const PRIMARY_GRADES = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'];
const SECONDARY_GRADES = ['S1', 'S2', 'S3', 'S4', 'S5', 'S6'];

const SCHOOL_TYPES = [
  { value: 'PRIMARY', label: 'Primary School' },
  { value: 'SECONDARY', label: 'Secondary School' },
];

interface Props {
  open: boolean;
  childProfile: ChildSchoolProfileSummary;
  onSave: (updated: ChildSchoolProfileSummary) => void;
  onClose: () => void;
}

const ProfileEditPopup: React.FC<Props> = ({ open, childProfile, onSave, onClose }) => {
  const [councils, setCouncils] = useState<ScottishCouncil[]>([]);
  const [schools, setSchools] = useState<ScottishSchool[]>([]);
  const [loadingCouncils, setLoadingCouncils] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);

  const [councilId, setCouncilId] = useState('');
  const [councilName, setCouncilName] = useState('');
  const [schoolType, setSchoolType] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [unknownCouncil, setUnknownCouncil] = useState(false);
  const [unknownCouncilName, setUnknownCouncilName] = useState('');
  const [unknownSchool, setUnknownSchool] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedWithUnknown, setSavedWithUnknown] = useState(false);

  // Load councils and restore saved values when dialog opens
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSavedWithUnknown(false);
    setUnknownCouncil(false);
    setUnknownCouncilName('');
    setUnknownSchool(false);
    setLoadingCouncils(true);
    scottishSchoolService.getCouncils()
      .then((data) => {
        setCouncils(data);
        // Pre-select council if we have a saved councilId
        const savedCouncilId = childProfile.councilId ? String(childProfile.councilId) : '';
        if (savedCouncilId && data.some(c => String(c.id) === savedCouncilId)) {
          const c = data.find(c => String(c.id) === savedCouncilId);
          setCouncilId(savedCouncilId);
          setCouncilName(c?.name ?? '');
        } else {
          setCouncilId('');
          setCouncilName('');
        }
        setSchoolType(childProfile.schoolType ?? '');
        setSchoolId('');
        setSchoolName('');
        setGradeLevel(childProfile.gradeLevel ?? '');
      })
      .catch(() => setError('Failed to load councils'))
      .finally(() => setLoadingCouncils(false));
  }, [open, childProfile]);

  // Load schools when council + school type are both set
  useEffect(() => {
    if (!councilId || !schoolType) {
      setSchools([]);
      return;
    }
    setLoadingSchools(true);
    scottishSchoolService.getSchools(Number(councilId), schoolType)
      .then((data) => {
        setSchools(data);
        // Try to match current school name in the loaded list
        const savedName = childProfile.schoolName ?? '';
        const match = data.find(s => s.name.toLowerCase() === savedName.toLowerCase());
        if (match) {
          setSchoolId(String(match.id));
          setSchoolName(match.name);
        }
      })
      .catch(() => setSchools([]))
      .finally(() => setLoadingSchools(false));
  }, [councilId, schoolType, childProfile.schoolName]);

  const handleCouncilChange = (value: string) => {
    if (value === UNKNOWN_COUNCIL) {
      setCouncilId(UNKNOWN_COUNCIL);
      setCouncilName('');
      setUnknownCouncil(true);
      setSchoolType('');
      setSchoolId('');
      setSchoolName('');
      setGradeLevel('');
      setSchools([]);
    } else {
      const c = councils.find(c => String(c.id) === value);
      setCouncilId(value);
      setCouncilName(c?.name ?? '');
      setUnknownCouncil(false);
      setUnknownCouncilName('');
      setSchoolType('');
      setSchoolId('');
      setSchoolName('');
      setGradeLevel('');
      setSchools([]);
    }
  };

  const handleSchoolTypeChange = (value: string) => {
    setSchoolType(value);
    setSchoolId('');
    setSchoolName('');
    setGradeLevel('');
    setSchools([]);
  };

  const handleSchoolChange = (value: string) => {
    if (value === SCHOOL_NOT_LISTED) {
      setSchoolId(SCHOOL_NOT_LISTED);
      setSchoolName('');
      setUnknownSchool(true);
      setGradeLevel('');
    } else {
      const s = schools.find(s => String(s.id) === value);
      setSchoolId(value);
      setSchoolName(s?.name ?? '');
      setUnknownSchool(false);
      setGradeLevel('');
    }
  };

  const gradeOptions = schoolType === 'PRIMARY' ? PRIMARY_GRADES : schoolType === 'SECONDARY' ? SECONDARY_GRADES : [];
  const hasGradeOrUnknown = !!gradeLevel || unknownCouncil;
  const canSave = (!!councilId && !!schoolType && !!schoolName && !!gradeLevel)
    || (unknownCouncil && !!unknownCouncilName.trim() && !!schoolName.trim())
    || (unknownSchool && !!schoolName.trim() && !!gradeLevel);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const request: ChildSchoolProfileUpdateRequest = {
        countryId: null,
        councilId: unknownCouncil ? null : Number(councilId),
        councilName: councilName || undefined,
        schoolName,
        gradeLevel: gradeLevel || schoolType,
        unknownSchool: unknownSchool || undefined,
        unknownCouncilName: unknownCouncilName || undefined,
      };
      const updated = await parentCourseService.updateSchoolProfile(childProfile.childId, request);
      const hadUnknown = unknownSchool || unknownCouncil;
      if (hadUnknown) {
        setSavedWithUnknown(true);
      } else {
        onSave(updated);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update school profile</DialogTitle>
      <DialogContent>
        {loadingCouncils ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && (
              <Typography color="error" variant="body2">{error}</Typography>
            )}

            {/* Country — Scotland only */}
            <FormControl fullWidth>
              <InputLabel>Country</InputLabel>
              <Select value="scotland" label="Country" disabled>
                <MenuItem value="scotland">Scotland</MenuItem>
              </Select>
            </FormControl>

            {/* Confirmation when saved with unknown location */}
            {savedWithUnknown && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Your school has been noted. An admin will confirm the details. You can access courses in the meantime.
              </Alert>
            )}

            {/* Council */}
            {!savedWithUnknown && (
              <FormControl fullWidth>
                <InputLabel>Council</InputLabel>
                <Select value={councilId} label="Council" onChange={(e) => handleCouncilChange(e.target.value)}>
                  {councils.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value={UNKNOWN_COUNCIL} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    My council isn't listed
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Unknown council picker */}
            {unknownCouncil && !savedWithUnknown && (
              <UnknownCouncilSchoolPicker
                onSchoolSelected={(s: ScottishSchoolWithCouncil) => {
                  setCouncilId(String(s.councilId));
                  setCouncilName(s.councilName);
                  setSchoolId(String(s.id));
                  setSchoolName(s.name);
                  setSchoolType(s.schoolType ?? '');
                  setUnknownCouncil(false);
                  setUnknownCouncilName('');
                  setUnknownSchool(false);
                }}
                onSchoolNotFound={(cn: string, st: string, sn: string) => {
                  setUnknownCouncilName(cn);
                  setSchoolType(st);
                  setSchoolName(sn);
                }}
              />
            )}

            {/* School Type */}
            {councilId && councilId !== UNKNOWN_COUNCIL && !unknownCouncil && !savedWithUnknown && (
              <FormControl fullWidth>
                <InputLabel>School Type</InputLabel>
                <Select value={schoolType} label="School Type" onChange={(e) => handleSchoolTypeChange(e.target.value)}>
                  {SCHOOL_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* School */}
            {councilId && councilId !== UNKNOWN_COUNCIL && schoolType && !savedWithUnknown && (
              <FormControl fullWidth disabled={loadingSchools}>
                <InputLabel>
                  {loadingSchools ? 'Loading schools…' : schools.length === 0 ? 'No schools found' : 'School'}
                </InputLabel>
                <Select value={schoolId} label="School" onChange={(e) => handleSchoolChange(e.target.value)}>
                  {schools.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value={SCHOOL_NOT_LISTED} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    My school isn't in this list
                  </MenuItem>
                </Select>
                {!loadingSchools && schools.length === 0 && (
                  <FormHelperText>No schools found for this council and type</FormHelperText>
                )}
                {!loadingSchools && !schoolId && childProfile.schoolName && (
                  <FormHelperText>Currently saved: {childProfile.schoolName}</FormHelperText>
                )}
              </FormControl>
            )}

            {/* Free-text school name for unknown school */}
            {unknownSchool && !savedWithUnknown && (
              <TextField
                fullWidth
                required
                label="School name"
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                helperText="Enter your school name — an admin will confirm the details"
              />
            )}

            {/* Grade / Year — filtered by school type */}
            {(schoolName || unknownCouncil) && !savedWithUnknown && (
              <FormControl fullWidth required>
                <InputLabel>Year / Grade</InputLabel>
                <Select value={gradeLevel} label="Year / Grade" onChange={(e) => setGradeLevel(e.target.value)}>
                  {gradeOptions.map((g) => (
                    <MenuItem key={g} value={g}>{g}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {savedWithUnknown ? 'Close' : 'Cancel'}
        </Button>
        {!savedWithUnknown && (
          <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProfileEditPopup;
